import React, { useEffect, useMemo, useState } from 'react';
import { FiMapPin, FiShield, FiActivity, FiFilter, FiGlobe } from 'react-icons/fi';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

export default function MonitorTrafico() {
  const { usuario } = useAuth();
  const esSuperadmin = usuario?.rol === 'superadmin';
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ desde: '', hasta: '', ip: '' });
  const [page, setPage] = useState(1);

  const cargarDatos = async (nextFilters = filters, nextPage = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (nextFilters.desde) params.set('desde', nextFilters.desde);
      if (nextFilters.hasta) params.set('hasta', nextFilters.hasta);
      if (nextFilters.ip) params.set('ip', nextFilters.ip);
      params.set('page', String(nextPage));
      params.set('limit', '20');

      const [summaryRes, logsRes] = await Promise.all([
        api.get(`/trafico/summary?${params.toString()}`),
        api.get(`/trafico/logs?${params.toString()}`),
      ]);

      setSummary(summaryRes.data);
      setLogs(logsRes.data.logs || []);
    } catch (err) {
      console.error('ERROR monitor tráfico ->', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (esSuperadmin) {
      cargarDatos();
    }
  }, [esSuperadmin]);

  const stats = useMemo(() => [
    { label: 'Peticiones totales', value: summary?.totalCalls ?? 0, icon: FiActivity },
    { label: 'IPs únicas', value: summary?.uniqueIpsTotal ?? 0, icon: FiGlobe },
    { label: 'Peticiones 24h', value: summary?.callsLast24h ?? 0, icon: FiFilter },
    { label: 'IPs 24h', value: summary?.uniqueIpsLast24h ?? 0, icon: FiMapPin },
  ], [summary]);

  const onChange = (field, value) => {
    const next = { ...filters, [field]: value };
    setFilters(next);
    setPage(1);
    cargarDatos(next, 1);
  };

  const resetFilters = () => {
    const clean = { desde: '', hasta: '', ip: '' };
    setFilters(clean);
    setPage(1);
    cargarDatos(clean, 1);
  };

  if (!esSuperadmin) {
    return (
      <div className="page">
        <div className="alert-warning"><FiShield /> Solo el superadmin puede acceder a la información de tráfico y geolocalización de IPs.</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Tráfico API</h1>
          <p className="page-subtitle">Registro de peticiones, IPs, localización y filtros del sistema</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <label style={{ fontWeight: 700, color: '#334155' }}>Desde</label>
          <input type="date" value={filters.desde} onChange={(e) => onChange('desde', e.target.value)} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #dbeafe' }} />
          <label style={{ fontWeight: 700, color: '#334155' }}>Hasta</label>
          <input type="date" value={filters.hasta} onChange={(e) => onChange('hasta', e.target.value)} style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #dbeafe' }} />
          <label style={{ fontWeight: 700, color: '#334155' }}>IP</label>
          <input type="text" value={filters.ip} onChange={(e) => onChange('ip', e.target.value)} placeholder="Ej. 8.8.8.8" style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid #dbeafe', minWidth: 180 }} />
          <button className="btn btn-secondary" onClick={resetFilters}>Limpiar</button>
        </div>
      </div>

      {loading ? (
        <p className="empty">Cargando tráfico…</p>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className="card stat-card">
                  <div className="stat-icon"><Icon /></div>
                  <div className="stat-label">{stat.label}</div>
                  <div className="stat-value">{stat.value}</div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div className="card">
              <h3 className="permisos-titulo">Top endpoints</h3>
              {(!summary?.topEndpoints || summary.topEndpoints.length === 0) ? (
                <p className="empty">Sin datos.</p>
              ) : (
                <div className="table-wrap">
                  <table className="data-table">
                    <thead><tr><th>Endpoint</th><th>Peticiones</th></tr></thead>
                    <tbody>
                      {summary.topEndpoints.map((item, index) => (
                        <tr key={`${item.endpoint}-${index}`}>
                          <td data-label="Endpoint">{item.endpoint}</td>
                          <td data-label="Peticiones">{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="card">
              <h3 className="permisos-titulo">Top IPs</h3>
              {(!summary?.topIps || summary.topIps.length === 0) ? (
                <p className="empty">Sin datos.</p>
              ) : (
                <div className="table-wrap">
                  <table className="data-table">
                    <thead><tr><th>IP</th><th>Peticiones</th></tr></thead>
                    <tbody>
                      {summary.topIps.map((item, index) => (
                        <tr key={`${item.ip}-${index}`}>
                          <td data-label="IP">{item.ip}</td>
                          <td data-label="Peticiones">{item.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="card" style={{ marginTop: 24 }}>
            <h3 className="permisos-titulo">Últimas peticiones</h3>
            {logs.length === 0 ? (
              <p className="empty">No hay peticiones registradas con esos filtros.</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Método</th>
                      <th>Endpoint</th>
                      <th>IP</th>
                      <th>País</th>
                      <th>Ciudad</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log._id || `${log.ip}-${log.timestamp}-${log.endpoint}`}>
                        <td data-label="Fecha">{log.timestamp ? new Date(log.timestamp).toLocaleString('es-ES') : '—'}</td>
                        <td data-label="Método"><span className="badge badge-info">{log.method}</span></td>
                        <td data-label="Endpoint">{log.endpoint}</td>
                        <td data-label="IP">{log.ip}</td>
                        <td data-label="País">{log.country || 'Desconocido'}</td>
                        <td data-label="Ciudad">{log.city || 'Desconocido'}</td>
                        <td data-label="Status"><span className={`badge ${Number(log.statusCode) >= 400 ? 'badge-danger' : 'badge-ok'}`}>{log.statusCode}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
              <button className="btn btn-secondary" disabled={page <= 1} onClick={() => { const next = page - 1; setPage(next); cargarDatos(filters, next); }}>Anterior</button>
              <button className="btn btn-primary" onClick={() => { const next = page + 1; setPage(next); cargarDatos(filters, next); }}>Siguiente</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
